// src/chatbot/contexts/ChatbotContext.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useBranch } from "~/contexts/BranchContext";
import {
  sendMessageToApi,
  sendMessageToStreamingApi,
} from "../services/chatbotApiService";
import type { MenuItem } from "~/types/new_types";
import type {
  ChatbotApiSuccessResponse,
  ChatbotCartItem,
  ChatbotCartOptionGroup,
  ChatbotCartSelection,
  ChatbotCartSnapshot,
  ChatbotMessage,
  ChatHistory,
  ParsedOrderItem,
  StreamCompletionResult,
} from "../types/chatbot";
import { Outlet } from "react-router-dom";
import {
  normalizeChatbotResponse,
  prepareStreamingChunk,
  resetStreamingSanitizerState,
} from "../utils/sanitizers";
import supabase, { createUserSession } from "~/utils/supabaseClient";
import { useShoppingCart } from "~/contexts/ShoppingCartContext";
import type { CartItem } from "~/contexts/ShoppingCartContext";
import {
  SessionStorageManager,
  type SessionStorageItemData,
} from "~/utils/sessionStorageManager";
import { getItemName, getItemDescription } from "~/utils/menuItemHelper";

const normalizeContentTypeForUi = (type?: string): string | undefined => {
  if (!type) {
    return undefined;
  }
  const lower = type.toLowerCase();
  if (lower.startsWith("audio/")) {
    return lower;
  }
  if (lower.includes("wav")) {
    return "audio/wav";
  }
  if (lower.includes("ogg")) {
    return "audio/ogg";
  }
  if (lower.includes("mp3") || lower.includes("mpeg")) {
    return "audio/mpeg";
  }
  return `audio/${lower}`;
};

const normalizeContentTypeForApi = (type?: string): "wav" | "ogg" | "mp3" | null => {
  if (!type) {
    return null;
  }
  const lower = type.toLowerCase();
  if (lower.includes("wav")) {
    return "wav";
  }
  if (lower.includes("ogg")) {
    return "ogg";
  }
  if (lower.includes("mp3") || lower.includes("mpeg")) {
    return "mp3";
  }
  return null;
};

interface ChatbotContextType {
  messages: ChatbotMessage[];
  isLoading: boolean;
  isOpen: boolean;
  unreadCount: number;

  isFullScreen: boolean;
  setIsFullScreen: React.Dispatch<React.SetStateAction<boolean>>;

  // Actions
  sendMessage: (message: string) => Promise<void>;
  sendStreamingMessage: (
    message: string,
    audioBase64?: string,
    contentType?: string
  ) => Promise<void>;
  clearMessages: () => void;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  markAsRead: () => void;

  // Utilities
  getItemDetails: (itemId: string) => MenuItem | undefined;
}

const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined);

const coerceCartAmount = (value: unknown): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

interface ChatbotProviderProps {
  useMockApi?: boolean;
  children?: ReactNode;
}

export const ChatbotProvider: React.FC<ChatbotProviderProps> = ({
  useMockApi = true,
  children,
}) => {
  const [messages, setMessages] = useState<ChatbotMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const suggestedItemsCacheRef = useRef<Map<string, MenuItem>>(new Map());
  const streamRequestRef = useRef<number>(0);

  const { branchData, menuItems, selectedCurrency } = useBranch();
  const {
    cartItems,
    cartSummary,
    shippingInfo,
    addToCart,
    addToCartWithVariants,
    getItemQuantity,
    getVariantQuantity,
    getVariantSubsetQuantity,
    setTempQuantity,
    removeFromCart,
    removeFromCartWithVariants,
    clearCart,
    updateShippingInfo,
    isCartOpen,
    isCartDisabled,
  } = useShoppingCart();
  const { i18n } = useTranslation();

  // Get item details by ID
  const getItemDetails = useCallback(
    (itemId: string): MenuItem | undefined => {
      return menuItems.find((item) => item.id === itemId);
    },
    [menuItems]
  );

  const resolveSuggestedItems = useCallback(
    async (itemsId: string[]): Promise<MenuItem[]> => {
      if (!itemsId || itemsId.length === 0) {
        return [];
      }

      const cache = suggestedItemsCacheRef.current;
      const uniqueIds = Array.from(new Set(itemsId));

      // Seed cache with items we already have locally
      uniqueIds.forEach((id) => {
        const item = getItemDetails(id);
        if (item) {
          cache.set(id, item);
        }
      });

      const missingIds = uniqueIds.filter((id) => !cache.has(id));

      if (missingIds.length > 0) {
        try {
          const { data, error } = await supabase
            .from("items")
            .select("*")
            .in("id", missingIds);

          if (error) {
            throw error;
          }

          (data ?? []).forEach((rawItem) => {
            const item = rawItem as MenuItem;
            if (item?.id) {
              cache.set(item.id, item);
            }
          });
        } catch (fetchError) {
          console.error("Failed to fetch suggested menu items:", fetchError);
        }
      }

      return itemsId
        .map((id) => cache.get(id))
        .filter((item): item is MenuItem => Boolean(item));
    },
    [getItemDetails]
  );
  const branchId: string = branchData?.branch.id || "";

  const convertCartVariantsToOptionGroups = useCallback(
    (
      variants: CartItem["variants"],
      quantity: number
    ): ChatbotCartOptionGroup[] => {
      if (!Array.isArray(variants) || variants.length === 0) {
        return [];
      }

      const groups = new Map<string, ChatbotCartOptionGroup>();

      variants.forEach((entry, index) => {
        if (!entry || typeof entry !== "object") {
          return;
        }

        const record = entry as Record<string, unknown>;
        const optionName =
          typeof record.optionName === "string" && record.optionName.trim()
            ? record.optionName.trim()
            : "";
        const variantName =
          typeof record.variantName === "string" && record.variantName.trim()
            ? record.variantName.trim()
            : optionName;

        if (!optionName || !variantName) {
          return;
        }

        const key = optionName.toLowerCase();
        const isMultiple = Boolean(
          record.isMultiple ?? record.multiple ?? record.is_multiple
        );
        const selectionType: ChatbotCartOptionGroup["selectionType"] =
          isMultiple ? "multiple" : "single";

        const group = groups.get(key) ?? {
          groupId:
            (typeof record.optionId === "string" && record.optionId.trim()) ||
            optionName.toLowerCase().replace(/\s+/g, "-") ||
            `group-${index + 1}`,
          groupName: optionName,
          selectionType,
          selections: [] as ChatbotCartSelection[],
        };

        if (selectionType === "multiple") {
          group.selectionType = "multiple";
        }

        const valueId =
          typeof record.valueId === "string" && record.valueId.trim()
            ? record.valueId.trim()
            : null;

        const variantPrice = Number.isFinite(
          Number(record.variantPrice ?? record.price ?? 0)
        )
          ? Number(record.variantPrice ?? record.price ?? 0)
          : 0;

        const selectionQuantity = Number.isFinite(Number(record.quantity))
          ? Math.max(1, Math.floor(Number(record.quantity)))
          : Math.max(1, quantity || 1);

        const variantType =
          typeof record.variantType === "string"
            ? record.variantType.toLowerCase()
            : undefined;
        const actionType =
          typeof record.actionType === "string"
            ? record.actionType.toLowerCase()
            : undefined;

        let valueName = variantName;
        const children = Array.isArray(record.children)
          ? record.children
          : undefined;

        if (
          variantType === "meals_and_combos" &&
          Array.isArray(children) &&
          children.length > 0
        ) {
          const includedSummaries = children
            .map((child) => {
              if (!child || typeof child !== "object") {
                return null;
              }
              const childRecord = child as Record<string, unknown>;
              const includedItems = Array.isArray(childRecord.includedItems)
                ? childRecord.includedItems
                : undefined;
              if (!includedItems || includedItems.length === 0) {
                return null;
              }

              const summary = includedItems
                .map((item) => {
                  if (!item || typeof item !== "object") {
                    return null;
                  }
                  const itemRecord = item as Record<string, unknown>;
                  const itemName =
                    typeof itemRecord.name === "string" &&
                      itemRecord.name.trim()
                      ? itemRecord.name.trim()
                      : null;
                  if (!itemName) {
                    return null;
                  }
                  const itemQuantity = Number.isFinite(
                    Number(itemRecord.quantity)
                  )
                    ? Math.max(1, Math.floor(Number(itemRecord.quantity)))
                    : 1;
                  const itemNote =
                    typeof itemRecord.note === "string" &&
                      itemRecord.note.trim()
                      ? ` (${itemRecord.note.trim()})`
                      : "";
                  return `${itemQuantity}x ${itemName}${itemNote}`;
                })
                .filter((entry): entry is string => Boolean(entry));

              return summary.length > 0 ? summary.join(", ") : null;
            })
            .filter((entry): entry is string => Boolean(entry));

          if (includedSummaries.length > 0) {
            valueName = `${variantName} [${includedSummaries.join(" | ")}]`;
          }
        }

        const priceDelta = variantPrice;

        group.selections.push({
          valueId,
          valueName,
          priceDelta,
          quantity: selectionQuantity,
        });

        groups.set(key, group);
      });

      return Array.from(groups.values());
    },
    []
  );

  const buildLineItemId = useCallback(
    (
      item: CartItem,
      options: ChatbotCartOptionGroup[],
      index: number
    ): string => {
      const baseId = item.id || `item-${index}`;
      const optionSignature = options
        .flatMap((group) =>
          group.selections.map((selection) =>
            `${group.groupName}:${selection.valueName}`.toLowerCase()
          )
        )
        .filter(Boolean)
        .sort()
        .join("|");
      if (!optionSignature) {
        return `${baseId}__base`;
      }
      const normalizedSignature = optionSignature.replace(
        /[^a-z0-9|_-]/gi,
        "_"
      );
      return `${baseId}__${normalizedSignature}`;
    },
    []
  );

  type StoredVariant = NonNullable<
    SessionStorageItemData["cards"]
  >[number]["variants"][number];
  type StoredVariantChild = StoredVariant["children"][number];

  const coerceSessionString = (value: unknown, fallback = ""): string => {
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : fallback;
    }
    if (value === null || value === undefined) {
      return fallback;
    }
    const stringified = String(value).trim();
    return stringified.length > 0 ? stringified : fallback;
  };

  const coerceSessionNumber = (value: unknown, fallback = 0): number => {
    const numeric = Number(typeof value === "string" ? value.trim() : value);
    return Number.isFinite(numeric) ? numeric : fallback;
  };

  const coerceNumberFromCandidates = (
    fallback: number,
    ...candidates: Array<unknown>
  ): number => {
    for (const candidate of candidates) {
      const numeric = Number(
        typeof candidate === "string" ? candidate.trim() : candidate
      );
      if (Number.isFinite(numeric)) {
        return numeric;
      }
    }
    return fallback;
  };

  const summarizeComboItems = (comboItems: unknown): string => {
    if (!Array.isArray(comboItems)) {
      return "";
    }
    const summary = comboItems
      .map((entry) => {
        if (!entry || typeof entry !== "object") {
          return "";
        }
        const record = entry as Record<string, unknown>;
        const itemName = coerceSessionString(
          record.item_name ?? record.name,
          ""
        );
        if (!itemName) {
          return "";
        }
        const quantity = coerceSessionNumber(record.quantity, 1);
        const note = coerceSessionString(record.note, "");
        const safeQuantity = quantity > 0 ? Math.floor(quantity) : 1;
        const noteSuffix = note ? ` (${note})` : "";
        return `${safeQuantity}x ${itemName}${noteSuffix}`;
      })
      .filter(Boolean);
    return summary.join(", ");
  };

  const buildOptionGroupsFromStoredVariants = (
    variants: StoredVariant[] | undefined,
    fallbackQuantity: number
  ): { groups: ChatbotCartOptionGroup[]; addOnTotal: number } => {
    if (!Array.isArray(variants) || variants.length === 0) {
      return { groups: [], addOnTotal: 0 };
    }

    const groups = new Map<string, ChatbotCartOptionGroup>();
    let addOnTotal = 0;

    variants.forEach((variant, variantIndex) => {
      if (!variant || variant.isVariant) {
        return;
      }

      const variantRecord = variant as Record<string, unknown>;
      const variantName = coerceSessionString(
        variantRecord.name,
        `Group ${variantIndex + 1}`
      );
      const rawGroupId = coerceSessionString(variantRecord.key, "");
      const normalizedGroupId =
        rawGroupId ||
        variantName.toLowerCase().replace(/\s+/g, "-") ||
        `group-${variantIndex + 1}`;

      const selectionType = variant.isMultiple ? "multiple" : "single";
      const group = groups.get(normalizedGroupId) ?? {
        groupId: normalizedGroupId,
        groupName: variantName,
        selectionType,
        selections: [] as ChatbotCartSelection[],
      };

      if (selectionType === "multiple") {
        group.selectionType = "multiple";
      }

      const children: StoredVariantChild[] = Array.isArray(variant.children)
        ? (variant.children as StoredVariantChild[])
        : [];

      children.forEach((child, childIndex) => {
        if (!child) {
          return;
        }
        const childRecord = child as Record<string, unknown>;
        const baseName = coerceSessionString(childRecord.name, "");
        const comboSummary = summarizeComboItems(
          (child as StoredVariantChild).comboData?.items
        );
        const valueName =
          comboSummary && baseName
            ? `${baseName} [${comboSummary}]`
            : comboSummary || baseName || `Option ${childIndex + 1}`;

        const isUpgradeVariant =
          (variant.variantType === "meals_and_combos" &&
            variant.actionType === "upgrade") ||
          (variant.variantType === "variants_and_options" &&
            variant.actionType === "upgrade");

        const pricePerUnit = isUpgradeVariant
          ? 0
          : coerceNumberFromCandidates(
            0,
            (child as StoredVariantChild).comboData?.combo_price,
            childRecord.price,
            childRecord.priceDelta,
            childRecord.price_delta,
            childRecord.unitPrice,
            childRecord.unit_price,
            childRecord.addonPrice,
            childRecord.addon_price
          );

        const selectionQuantity = Number.isFinite(variant.quantity)
          ? Math.max(1, Math.floor(Number(variant.quantity)))
          : Math.max(1, fallbackQuantity);

        addOnTotal += pricePerUnit * selectionQuantity;

        group.selections.push({
          valueId: null,
          valueName,
          priceDelta: pricePerUnit,
          quantity: selectionQuantity,
        });
      });

      if (group.selections.length > 0) {
        groups.set(normalizedGroupId, group);
      }
    });

    return {
      groups: Array.from(groups.values()),
      addOnTotal,
    };
  };

  const buildChatbotCartSnapshot = useCallback((): ChatbotCartSnapshot => {
    const currencyId = selectedCurrency?.id ?? "";
    const currencyName = selectedCurrency?.iso ?? selectedCurrency?.name ?? "";
    const orderNotes =
      shippingInfo.orderNote ?? SessionStorageManager.getOrderNote() ?? "";

    const branchItemIds = new Set(
      (branchData?.items ?? []).map((menuItem) => menuItem.id)
    );

    const sessionItemsRecord = SessionStorageManager.getAllItems();
    const sessionItems = Object.values(
      sessionItemsRecord
    ) as SessionStorageItemData[];

    const relevantSessionItems = sessionItems.filter((item) => {
      if (!item) {
        return false;
      }
      const totalQuantity = Number.isFinite(item.totalQuantity)
        ? Math.max(0, Math.floor(Number(item.totalQuantity)))
        : 0;
      if (totalQuantity <= 0) {
        return false;
      }
      if (branchItemIds.size > 0 && !branchItemIds.has(item.itemId)) {
        return false;
      }
      return true;
    });

    if (relevantSessionItems.length > 0) {
      console.log(
        "[ChatbotContext] Building cart snapshot from session storage",
        {
          totalItems: relevantSessionItems.length,
          branchId,
        }
      );

      const items: ChatbotCartItem[] = [];

      relevantSessionItems.forEach((sessionItem) => {
        const defaultBasePrice = Number.isFinite(sessionItem.basePrice)
          ? Number(sessionItem.basePrice)
          : 0;
        // Get first note from notes array for AI cart snapshot
        const itemNote =
          Array.isArray(sessionItem.itemNotes) && sessionItem.itemNotes.length > 0
            ? sessionItem.itemNotes[0]
            : undefined;
        const isAiSuggestion = Boolean(sessionItem.cards?.[0]?.isAiSuggestion);
        const appendSessionEntry = (
          lineItemId: string,
          quantity: number,
          unitBasePrice: number,
          unitAddOnTotal: number,
          variants: StoredVariant[] | undefined
        ) => {
          if (quantity <= 0) {
            return;
          }

          const { groups, addOnTotal } = buildOptionGroupsFromStoredVariants(
            variants,
            quantity
          );

          const expectedAddOnTotal = unitAddOnTotal * quantity;
          const addOnDifference = expectedAddOnTotal - addOnTotal;
          let resolvedGroups = groups;

          if (Math.abs(addOnDifference) > 0.01 && quantity > 0) {
            const perUnitAdjustment = Number.parseFloat(
              (addOnDifference / quantity).toFixed(2)
            );
            if (Math.abs(perUnitAdjustment) > 0.01) {
              resolvedGroups = [
                ...groups,
                {
                  groupId: `${lineItemId}-adjustment`,
                  groupName: "Additional Customizations",
                  selectionType: "single",
                  selections: [
                    {
                      valueId: null,
                      valueName: "Adjustments",
                      priceDelta: perUnitAdjustment,
                      quantity,
                    },
                  ],
                },
              ];
            }
          }

          items.push({
            lineItemId,
            productId: sessionItem.itemId,
            quantity,
            basePrice: Number.parseFloat(unitBasePrice.toFixed(2)),
            item_notes: itemNote ?? null,
            options: resolvedGroups,
            isAiSuggestion
          });
        };

        if (Array.isArray(sessionItem.cards) && sessionItem.cards.length > 0) {
          sessionItem.cards.forEach((card, index) => {
            const quantity = Number.isFinite(card.quantity)
              ? Math.max(0, Math.floor(Number(card.quantity)))
              : 0;
            if (quantity <= 0) {
              return;
            }

            const pricing = SessionStorageManager.calculateCardPricingSummary(
              card.variants,
              defaultBasePrice
            );

            const unitBasePrice = Number.isFinite(card.unitBasePrice)
              ? Number(card.unitBasePrice)
              : pricing.unitBasePrice;
            const unitAddOnTotal = Number.isFinite(card.unitAddOnTotal)
              ? Number(card.unitAddOnTotal)
              : pricing.unitAddOnTotal;

            appendSessionEntry(
              card.cardId || `${sessionItem.itemId}-card-${index + 1}`,
              quantity,
              unitBasePrice,
              unitAddOnTotal,
              card.variants as StoredVariant[] | undefined
            );
          });
        } else {
          const quantity = Number.isFinite(sessionItem.totalQuantity)
            ? Math.max(0, Math.floor(Number(sessionItem.totalQuantity)))
            : 0;
          if (quantity <= 0) {
            return;
          }

          const pricing = SessionStorageManager.calculateCardPricingSummary(
            sessionItem.variants as StoredVariant[],
            defaultBasePrice
          );

          appendSessionEntry(
            `${sessionItem.itemId}-legacy`,
            quantity,
            pricing.unitBasePrice,
            pricing.unitAddOnTotal,
            sessionItem.variants as StoredVariant[] | undefined
          );
        }
      });

      const filteredItems = items.filter((entry) => entry.quantity > 0);


      const totalQuantity = filteredItems.reduce(
        (sum, entry) => sum + entry.quantity,
        0
      );

      const computedTotal = filteredItems.reduce((sum: number, entry) => {
        const optionsTotal = entry.options.reduce(
          (groupSum: number, group) =>
            groupSum +
            group.selections.reduce(
              (selectionSum: number, selection) =>
                selectionSum + selection.priceDelta * selection.quantity,
              0
            ),
          0
        );
        return sum + entry.basePrice * entry.quantity + optionsTotal;
      }, 0);

      const sessionTotal = SessionStorageManager.getTotalCartValue();
      const normalizedComputedTotal = Number.parseFloat(
        computedTotal.toFixed(2)
      );
      const normalizedSessionTotal = Number.parseFloat(sessionTotal.toFixed(2));

      const total =
        Math.abs(normalizedSessionTotal - normalizedComputedTotal) > 0.01
          ? normalizedSessionTotal
          : normalizedComputedTotal;

      return {
        currencyId,
        currencyName,
        totalQuantity,
        total,
        order_notes: orderNotes,
        items: filteredItems,


      };
    }

    console.log("[ChatbotContext] Building cart snapshot from cartItems", {
      totalCartItems: cartItems.length,
      branchId,
    });

    const relevantCartItems = cartItems.filter((item) => {
      const quantity = Number.isFinite(item.quantity)
        ? Math.max(0, Math.floor(item.quantity))
        : 0;
      if (quantity <= 0) {
        return false;
      }
      if (branchId && item.restaurantId && item.restaurantId !== branchId) {
        return false;
      }
      return true;
    });

    const fallbackItems = relevantCartItems
      .map((item, index) => {
        const quantity = Number.isFinite(item.quantity)
          ? Math.max(0, Math.floor(item.quantity))
          : 0;
        if (quantity <= 0) {
          return null;
        }
        const basePrice = coerceCartAmount(item.discounted_price ?? item.price);
        const optionGroups = convertCartVariantsToOptionGroups(
          item.variants,
          quantity
        );
        return {
          lineItemId: buildLineItemId(item, optionGroups, index),
          productId: item.id,
          quantity,
          basePrice,
          item_notes: item.specialInstructions ?? null,
          options: optionGroups,
          isAiSuggestion: item.isAiSuggestion ?? true,
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));

    const totalQuantity = fallbackItems.reduce(
      (sum, item) => sum + (Number.isFinite(item.quantity) ? item.quantity : 0),
      0
    );

    const subtotal = relevantCartItems.reduce((sum, item) => {
      const quantity = Number.isFinite(item.quantity)
        ? Math.max(0, Math.floor(item.quantity))
        : 0;
      if (quantity <= 0) {
        return sum;
      }
      const unitPrice = coerceCartAmount(item.discounted_price ?? item.price);
      return sum + unitPrice * quantity;
    }, 0);
    const summaryTotalNumeric = Number(cartSummary.total);
    const hasValidSummaryTotal = Number.isFinite(summaryTotalNumeric);
    const shouldUseSummaryTotal =
      relevantCartItems.length === cartItems.length && hasValidSummaryTotal;
    const totalRaw = shouldUseSummaryTotal ? summaryTotalNumeric : subtotal;
    const totalAmount = Number.parseFloat(totalRaw.toFixed(2));

    return {
      currencyId,
      currencyName,
      totalQuantity,
      total: Number.isFinite(totalAmount) ? totalAmount : 0,
      order_notes: orderNotes,
      items: fallbackItems,
    };
  }, [
    branchData,
    branchId,
    cartItems,
    cartSummary.total,
    convertCartVariantsToOptionGroups,
    buildLineItemId,
    selectedCurrency,
    shippingInfo.orderNote,
  ]);

  const convertOptionsToCartVariants = useCallback(
    (
      options: ChatbotCartOptionGroup[] | undefined
    ): Array<{
      optionName: string;
      variantName: string;
      variantPrice: number;
      isVariant: boolean;
      isMultiple: boolean;
      variantType?: string;
      actionType?: string;
      quantity: number;
      optionId?: string;
      children?: Array<{
        name: string;
        price: number;
        comboData?: {
          items: Array<{
            item_name: string;
            quantity: number;
            note?: string;
          }>;
          combo_price: number;
        };
        includedItems?: Array<{
          name: string;
          quantity: number;
          note?: string;
        }>;
      }>;
    }> => {
      if (!Array.isArray(options) || options.length === 0) {
        return [];
      }
      const parseSelectionLabel = (label: string) => {
        const text = label.trim();
        const match = text.match(/^(.*?)(?:\s*\[(.*)\])?$/);
        const baseName = match && match[1] ? match[1].trim() : text;
        const items: Array<{ name: string; quantity: number; note?: string }> =
          [];

        const listText = match && match[2] ? match[2].trim() : "";
        if (listText) {
          listText.split(/,\s*/).forEach((segment) => {
            const part = segment.trim();
            if (!part) {
              return;
            }
            const quantityMatch = part.match(/^(\d+)x\s*(.*)$/i);
            let quantity = 1;
            let remainder = part;
            if (quantityMatch) {
              quantity = Number.parseInt(quantityMatch[1], 10) || 1;
              remainder = quantityMatch[2] ?? "";
            }
            remainder = remainder.trim();
            if (!remainder) {
              return;
            }
            let note: string | undefined;
            const noteMatch = remainder.match(/\(([^)]+)\)\s*$/);
            if (noteMatch && typeof noteMatch.index === "number") {
              note = noteMatch[1].trim();
              remainder = remainder.slice(0, noteMatch.index).trim();
            }
            if (!remainder) {
              return;
            }
            items.push({ name: remainder, quantity, note });
          });
        }

        return { baseName, items };
      };

      const variantList: Array<{
        optionName: string;
        variantName: string;
        variantPrice: number;
        isVariant: boolean;
        isMultiple: boolean;
        variantType?: string;
        actionType?: string;
        quantity: number;
        optionId?: string;
        children?: Array<{
          name: string;
          price: number;
          comboData?: {
            items: Array<{
              item_name: string;
              quantity: number;
              note?: string;
            }>;
            combo_price: number;
          };
          includedItems?: Array<{
            name: string;
            quantity: number;
            note?: string;
          }>;
        }>;
      }> = [];
      options.forEach((group) => {
        if (!group || typeof group !== "object") {
          return;
        }
        const optionName = group.groupName?.trim();
        if (!optionName) {
          return;
        }
        const optionId = group.groupId?.trim();
        const normalizedGroupId = (optionId || optionName).toLowerCase();
        const looksLikeCombo = /combo|meal/.test(normalizedGroupId);
        const variantType = looksLikeCombo
          ? "meals_and_combos"
          : "variants_and_options";
        const actionType = looksLikeCombo ? "upgrade" : "selection";
        const isMultiple = group.selectionType === "multiple";
        (group.selections ?? []).forEach((selection) => {
          if (!selection || typeof selection !== "object") {
            return;
          }
          const variantName = selection.valueName?.trim();
          if (!variantName) {
            return;
          }
          const { baseName, items } = parseSelectionLabel(variantName);
          const priceDelta = Number.isFinite(selection.priceDelta)
            ? Number(selection.priceDelta)
            : 0;
          const selectionQuantity = Number.isFinite(selection.quantity)
            ? Math.max(1, Math.floor(Number(selection.quantity)))
            : 1;

          const child: {
            name: string;
            price: number;
            comboData?: {
              items: Array<{
                item_name: string;
                quantity: number;
                note?: string;
              }>;
              combo_price: number;
            };
            includedItems?: Array<{
              name: string;
              quantity: number;
              note?: string;
            }>;
          } = {
            name: baseName,
            price: priceDelta,
          };

          if (items.length > 0) {
            child.comboData = {
              items: items.map((item) => ({
                item_name: item.name,
                quantity: item.quantity,
                note: item.note,
              })),
              combo_price: priceDelta,
            };
            child.includedItems = items.map((item) => ({
              name: item.name,
              quantity: item.quantity,
              note: item.note,
            }));
          }

          variantList.push({
            optionName,
            optionId,
            variantName: baseName,
            variantPrice: priceDelta,
            isVariant: false,
            isMultiple,
            variantType,
            actionType,
            quantity: selectionQuantity,
            children: [child],
          });
        });
      });
      return variantList;
    },
    []
  );

  // Convert chatbot options format to session storage format
  const convertChatbotOptionsToSessionFormat = useCallback(
    (
      options: ChatbotCartOptionGroup[] | undefined,
      menuItem: MenuItem
    ): Array<any> => {
      if (!Array.isArray(options) || options.length === 0) {
        return [];
      }

      console.log(
        "[ChatbotContext] Converting chatbot options to session format:",
        { options, menuItem }
      );

      const sessionVariants: Array<any> = [];

      options.forEach((optionGroup) => {
        if (
          !optionGroup ||
          !optionGroup.groupId ||
          !Array.isArray(optionGroup.selections)
        ) {
          return;
        }

        const children: Array<any> = [];
        let actionType = "selection"; // Default to selection (additive)
        let variantType = "variants_and_options";

        // Check if this is a combo/upgrade by examining the option group structure
        // Look for combo patterns in the selections
        const hasComboPattern = optionGroup.selections.some(
          (selection) =>
            selection.valueName &&
            (selection.valueName.toLowerCase().includes("combo") ||
              selection.valueName.includes("[") ||
              (selection.priceDelta && selection.priceDelta > 50)) // Large price delta suggests combo
        );

        if (hasComboPattern) {
          actionType = "upgrade";
          variantType = "meals_and_combos";
        }

        optionGroup.selections.forEach((selection) => {
          if (!selection || !selection.valueName) {
            return;
          }

          const price = Number(selection.priceDelta || 0);
          const valueName = selection.valueName;

          // Check if this looks like a combo with items listed
          if (valueName.includes("[") && valueName.includes("]")) {
            // Parse combo format: "Combo margherita [1x Pepsi (sugar free), 2x 7Up (700ml)]"
            const match = valueName.match(/^(.*?)\s*\[(.*)\]\s*$/);
            if (match) {
              const comboName = match[1].trim();
              const itemsList = match[2].trim();

              // Parse individual items
              const comboItems: Array<any> = [];
              itemsList.split(",").forEach((itemStr) => {
                const itemMatch = itemStr
                  .trim()
                  .match(/^(\d+)x\s*([^()]+)(?:\(([^)]+)\))?/);
                if (itemMatch) {
                  const quantity = parseInt(itemMatch[1]) || 1;
                  const itemName = itemMatch[2].trim();
                  const note = itemMatch[3] ? itemMatch[3].trim() : undefined;

                  comboItems.push({
                    item_name: itemName,
                    quantity: quantity,
                    note: note,
                  });
                }
              });

              children.push({
                name: comboName,
                price: price,
                imageUrl: "",
                comboData: {
                  items: comboItems,
                  combo_price: price,
                },
              });
            } else {
              // Fallback if parsing fails
              children.push({
                name: valueName,
                price: price,
                imageUrl: "",
              });
            }
          } else {
            // Regular selection
            children.push({
              name: valueName,
              price: price,
              imageUrl: "",
            });
          }
        });

        if (children.length > 0) {
          sessionVariants.push({
            optionName:
              optionGroup.groupName || `Option ${optionGroup.groupId}`, // Use groupName if available, fallback to groupId
            variantName: children.map((c) => c.name).join(", "),
            variantPrice: children.reduce((sum, c) => sum + (c.price || 0), 0),
            variantType: variantType,
            actionType: actionType,
            isVariant: false,
            isMultiple: children.length > 1,
            quantity: 1,
            optionId: optionGroup.groupId,
            children: children,
          });
        }
      });

      console.log(
        "[ChatbotContext] Converted session variants:",
        sessionVariants
      );
      return sessionVariants;
    },
    []
  );

  const syncCartWithSnapshot = useCallback(
    (snapshot: ChatbotCartSnapshot | null, fallbackItemIds?: string[]) => {
      console.log(
        "[ChatbotContext] syncCartWithSnapshot called with:",
        snapshot
      );
      if (!snapshot) {
        return;
      }
      if (!branchId) {
        return;
      }
      if (isCartDisabled) {
        console.warn(
          "Cart functionality is disabled for this branch - chatbot cannot override cart"
        );
        return;
      }

      const snapshotItems = Array.isArray(snapshot.items) ? snapshot.items : [];
      const fallbackQueue = Array.isArray(fallbackItemIds)
        ? Array.from(
          new Set(
            fallbackItemIds
              .filter((id) => typeof id === "string")
              .map((id) => (typeof id === "string" ? id.trim() : ""))
              .filter((id) => id.length > 0)
          )
        )
        : [];
      const usedFallbackIds = new Set<string>();

      // Get current session state for comparison
      const currentSessionItems = SessionStorageManager.getAllItems();
      const currentProductIds = new Set(Object.keys(currentSessionItems));

      // Track which product IDs are in the AI snapshot
      const snapshotProductIds = new Set<string>();

      // Build a map of snapshot items by productId for easier lookup
      const snapshotItemMap = new Map<string, typeof snapshotItems[number]>();
      snapshotItems.forEach((item) => {
        if (item && item.productId) {
          snapshotProductIds.add(item.productId);
          snapshotItemMap.set(item.productId, item);
        }
      });

      console.log("[ChatbotContext] Smart merge - comparing:", {
        currentProductIds: Array.from(currentProductIds),
        snapshotProductIds: Array.from(snapshotProductIds),
      });

      // Remove items that are in session but NOT in AI snapshot
      currentProductIds.forEach((productId) => {
        if (!snapshotProductIds.has(productId)) {
          console.log(`[ChatbotContext] Removing item ${productId} - not in AI snapshot`);
          SessionStorageManager.removeItem(productId);
        }
      });

      // Remove items that ARE in the snapshot (we'll rebuild them fresh)
      // This ensures we don't have stale variant configurations
      snapshotProductIds.forEach((productId) => {
        if (currentProductIds.has(productId)) {
          console.log(`[ChatbotContext] Removing item ${productId} - will be rebuilt from AI snapshot`);
          SessionStorageManager.removeItem(productId);
        }
      });

      // Clear cart state before rebuilding (needed for ShoppingCart sync)
      clearCart();

      // Track which productIds have already been processed to avoid double-removing
      const processedProductIds = new Set<string>();

      // Process each item in the AI snapshot
      snapshotItems.forEach((item) => {
        if (!item || typeof item !== "object" || !item.productId) {
          return;
        }

        console.log('[ChatbotContext] Processing cart item:', {
          productId: item.productId,
          lineItemId: item.lineItemId,
          isAiSuggestion: item.isAiSuggestion,
          typeof_isAiSuggestion: typeof item.isAiSuggestion,
          quantity: item.quantity,
          item_notes: item.item_notes,
          options: item.options,
        });

        // Extract isAiSuggestion from cart item - use AI's exact value
        const isAiSuggestion = typeof item.isAiSuggestion === 'boolean'
          ? item.isAiSuggestion
          : false; // Default to false if not specified (assume manual)

        console.log('[ChatbotContext] Extracted isAiSuggestion:', isAiSuggestion);

        let menuItem = getItemDetails(item.productId);
        if (!menuItem && fallbackQueue.length > 0) {
          const fallbackId = fallbackQueue.find((candidateId) => {
            if (usedFallbackIds.has(candidateId)) {
              return false;
            }
            const candidate = getItemDetails(candidateId);
            if (candidate) {
              usedFallbackIds.add(candidateId);
              menuItem = candidate;
              return true;
            }
            return false;
          });
          if (!menuItem && fallbackId) {
            menuItem = getItemDetails(fallbackId);
          }
        }
        if (!menuItem) {
          console.warn(
            "Chatbot returned unknown product in cart snapshot:",
            item.productId
          );
          return;
        }

        const quantity = Number.isFinite(item.quantity)
          ? Math.max(1, Math.floor(item.quantity))
          : 1;

        // Convert chatbot options to session storage format
        const sessionVariants = convertChatbotOptionsToSessionFormat(
          item.options,
          menuItem
        );

        // Check if this item has combo/upgrade variants
        const hasUpgradeVariant = sessionVariants.some(
          (v: any) =>
            v.actionType === "upgrade" ||
            (v.variantType === "meals_and_combos" && v.actionType === "upgrade")
        );

        // Only use chatbot's basePrice if it's NOT a combo/upgrade
        // For combos, the combo price is stored in comboData.combo_price, not as basePrice
        const menuItemWithCorrectPrice = {
          ...menuItem,
          price:
            !hasUpgradeVariant && typeof item.basePrice === "number"
              ? item.basePrice
              : menuItem.price,
          discounted_price: null,
        };

        const noteProvided = Object.prototype.hasOwnProperty.call(
          item,
          "item_notes"
        );
        const resolvedItemNote =
          typeof item.item_notes === "string" ? item.item_notes : "";
        const cartItemNote = noteProvided ? resolvedItemNote : undefined;

        // Mark this productId as processed (no need to remove again)
        processedProductIds.add(item.productId);

        console.log('[ChatbotContext] Adding item to session storage:', {
          productId: item.productId,
          isAiSuggestion,
          quantity,
          variantsCount: sessionVariants.length,
        });

        // Add to session storage - this is from AI, so use AI's isAiSuggestion value
        // Note: We already removed the item above, so this will add a new card
        SessionStorageManager.addOrUpdateItemWithVariants(
          menuItemWithCorrectPrice,
          i18n.language,
          sessionVariants,
          quantity,
          noteProvided ? resolvedItemNote : undefined,
          isAiSuggestion
        );

        // Also update temp quantities for UI sync
        setTempQuantity(item.productId, quantity, sessionVariants);

        // Add to cart using existing methods for backward compatibility
        if (sessionVariants.length > 0) {
          const cartVariants = convertOptionsToCartVariants(item.options);
          addToCartWithVariants(
            menuItemWithCorrectPrice,
            branchId,
            quantity,
            cartVariants,
            cartItemNote,
            isAiSuggestion
          );
        } else {
          addToCart(menuItemWithCorrectPrice, branchId, quantity, cartItemNote, isAiSuggestion);
        }
      });

      updateShippingInfo({ orderNote: snapshot.order_notes ?? "" });
    },
    [
      addToCart,
      addToCartWithVariants,
      branchId,
      clearCart,
      convertChatbotOptionsToSessionFormat,
      convertOptionsToCartVariants,
      getItemDetails,
      i18n.language,
      isCartDisabled,
      setTempQuantity,
      updateShippingInfo,
    ]
  );

  const handleOrderItems = useCallback(
    (orderItems: ParsedOrderItem[]) => {
      if (!branchId || orderItems.length === 0) {
        return;
      }

      // Early return if cart is disabled
      if (isCartDisabled) {
        console.warn(
          "Cart functionality is disabled for this branch - chatbot cannot add items to cart"
        );
        return;
      }

      orderItems.forEach((orderItem) => {
        if (!orderItem?.id) {
          return;
        }

        const menuItem = getItemDetails(orderItem.id);
        if (!menuItem) {
          console.warn(
            "Chatbot attempted to add unknown item to cart:",
            orderItem.id
          );
          return;
        }

        const quantity = Number.isFinite(orderItem.quantity)
          ? Math.max(1, Math.floor(orderItem.quantity))
          : 1;
        const totalQuantityBefore = getItemQuantity(orderItem.id);
        const baseQuantityBefore = getVariantQuantity(orderItem.id, []);
        const exactVariantQuantityBefore = getVariantQuantity(
          orderItem.id,
          orderItem.variants ?? []
        );
        const subsetVariantQuantityBefore = getVariantSubsetQuantity(
          orderItem.id,
          orderItem.variants ?? []
        );
        const isConvertingBaseToVariant =
          Array.isArray(orderItem.variants) &&
          orderItem.variants.length > 0 &&
          baseQuantityBefore > 0 &&
          exactVariantQuantityBefore === 0;
        const isVariantReplacement =
          Array.isArray(orderItem.variants) &&
          orderItem.variants.length > 0 &&
          subsetVariantQuantityBefore > 0 &&
          exactVariantQuantityBefore === 0;

        if (orderItem.variants && orderItem.variants.length > 0) {
          addToCartWithVariants(
            menuItem,
            branchId,
            quantity,
            orderItem.variants,
            undefined,
            true // isAiSuggestion
          );
          SessionStorageManager.addOrUpdateItemWithVariants(
            menuItem,
            i18n.language,
            orderItem.variants,
            quantity,
            undefined,
            true // isAiSuggestion
          );
          const remainingNonBaseQuantity = Math.max(
            0,
            totalQuantityBefore - baseQuantityBefore
          );
          let desiredTempQuantity = totalQuantityBefore + quantity;
          if (isVariantReplacement) {
            desiredTempQuantity =
              totalQuantityBefore - subsetVariantQuantityBefore + quantity;
          } else if (isConvertingBaseToVariant) {
            desiredTempQuantity = remainingNonBaseQuantity + quantity;
          }
          setTempQuantity(
            orderItem.id,
            desiredTempQuantity,
            orderItem.variants
          );
        } else {
          addToCart(menuItem, branchId, quantity, undefined, true); // isAiSuggestion
          SessionStorageManager.addOrIncrementItem(
            menuItem,
            i18n.language,
            quantity,
            undefined,
            true // isAiSuggestion
          );
          setTempQuantity(orderItem.id, totalQuantityBefore + quantity, []);
        }
      });
    },
    [
      addToCart,
      addToCartWithVariants,
      branchId,
      getItemDetails,
      getItemQuantity,
      getVariantQuantity,
      getVariantSubsetQuantity,
      i18n.language,
      setTempQuantity,
    ]
  );

  const handleRemovedItems = useCallback(
    (removedItems: ParsedOrderItem[]) => {
      if (!branchId || removedItems.length === 0) {
        return;
      }

      // Early return if cart is disabled
      if (isCartDisabled) {
        console.warn(
          "Cart functionality is disabled for this branch - chatbot cannot remove items from cart"
        );
        return;
      }

      removedItems.forEach((removedItem) => {
        if (!removedItem?.id) {
          return;
        }

        const menuItem = getItemDetails(removedItem.id);
        if (!menuItem) {
          console.warn(
            "Chatbot attempted to remove unknown item from cart:",
            removedItem.id
          );
          return;
        }

        const totalQuantityBefore = getItemQuantity(removedItem.id);

        if (removedItem.variants && removedItem.variants.length > 0) {
          const exactVariantQuantityBefore = getVariantQuantity(
            removedItem.id,
            removedItem.variants
          );
          const subsetVariantQuantityBefore = getVariantSubsetQuantity(
            removedItem.id,
            removedItem.variants
          );

          // Check exact match first
          if (exactVariantQuantityBefore > 0) {
            // For exact variant matches, we want to convert to base item (remove variants but keep item)
            // Use quantity = 0 to indicate variant-only removal
            removeFromCartWithVariants(
              removedItem.id,
              removedItem.variants,
              0 // Use 0 to trigger variant-only removal
            );
            SessionStorageManager.removeItemVariants(
              removedItem.id,
              removedItem.variants,
              0
            );

            // Keep the same total quantity but with no variants
            setTempQuantity(removedItem.id, totalQuantityBefore, []);
          } else if (subsetVariantQuantityBefore > 0) {
            // Only do subset removal if there's no exact match
            removeFromCartWithVariants(removedItem.id, removedItem.variants, 0);
            SessionStorageManager.removeItemVariants(
              removedItem.id,
              removedItem.variants,
              0
            );
            setTempQuantity(removedItem.id, totalQuantityBefore, []);
          } else {
            return;
          }
        } else {
          const requestedQuantity = Number.isFinite(removedItem.quantity)
            ? Math.max(1, Math.floor(removedItem.quantity as number))
            : 1;
          const quantityToRemove = Math.min(
            requestedQuantity,
            totalQuantityBefore || requestedQuantity
          );

          removeFromCart(removedItem.id);
          SessionStorageManager.removeItem(removedItem.id);
          setTempQuantity(
            removedItem.id,
            Math.max(0, totalQuantityBefore - quantityToRemove),
            []
          );
        }
      });
    },
    [
      branchId,
      getItemDetails,
      getItemQuantity,
      getVariantQuantity,
      getVariantSubsetQuantity,
      removeFromCart,
      removeFromCartWithVariants,
      setTempQuantity,
    ]
  );

  useEffect(() => {
    suggestedItemsCacheRef.current.clear();
  }, [branchId]);

  // Send streaming message to chatbot
  const sendStreamingMessage = async (
    message: string,
    audioBase64?: string,
    contentType?: string
  ) => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage && !audioBase64) return;
    if (!branchId) return;

    const uiContentType = audioBase64
      ? normalizeContentTypeForUi(contentType)
      : undefined;
    const apiContentType = normalizeContentTypeForApi(
      contentType ?? uiContentType
    );
    const userDisplayText = audioBase64 ? "Voice message" : trimmedMessage;

    // Add user message
    const userMessage: ChatbotMessage = {
      id: `user-${Date.now()}`,
      text: userDisplayText,
      isUser: true,
      timestamp: new Date(),
      audioBase64: audioBase64 ?? undefined,
      contentType: uiContentType,
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    // Create bot message for streaming (but don't add to messages yet)
    const botMessageId = `bot-${Date.now()}`;
    const botMessage: ChatbotMessage = {
      id: botMessageId,
      text: "",
      isUser: false,
      timestamp: new Date(),
      isStreaming: true,
      suggestedItemsIds: [],
      suggestedItems: [],
    };

    let messageAdded = false;

    const updateBotMessage = (
      updater: (message: ChatbotMessage) => ChatbotMessage
    ) => {
      setMessages((prev) => {
        let found = false;
        const next = prev.map((msg) => {
          if (msg.id === botMessageId) {
            found = true;
            return updater(msg);
          }
          return msg;
        });

        if (!found) {
          const updated = updater({ ...botMessage });
          // Only add the message if it has actual content
          if (updated.text.trim() || messageAdded) {
            messageAdded = true;
            next.push(updated);
          }
        }

        return next;
      });
    };

    try {
      // Use existing session ID from localStorage
      const sessionId = localStorage.getItem("session_id");

      // Ensure we have a valid session ID
      if (!sessionId) {
        throw new Error("No session ID found - session may not be initialized yet");
      }

      const cartSnapshot = buildChatbotCartSnapshot();
      const streamToken = Date.now();
      streamRequestRef.current = streamToken;

      resetStreamingSanitizerState();

      await sendMessageToStreamingApi(
        trimmedMessage,
        branchId,
        sessionId,
        cartSnapshot,
        // On chunk received
        (chunk: string) => {
          if (streamRequestRef.current !== streamToken) {
            return;
          }

          updateBotMessage((prevMessage) => {
            const cleanChunk = prepareStreamingChunk(chunk, prevMessage.text);
            if (!cleanChunk) {
              return prevMessage;
            }

            // Trim leading whitespace only if the message is still empty
            const newText = prevMessage.text 
              ? `${prevMessage.text}${cleanChunk}` 
              : cleanChunk.trimStart();

            return {
              ...prevMessage,
              text: newText,
            };
          });
        },
        // On complete
        (result: StreamCompletionResult) => {
          if (streamRequestRef.current !== streamToken) {
            return;
          }

          void (async () => {
            try {
              if (streamRequestRef.current !== streamToken) {
                return;
              }

              if (result.cart) {
                syncCartWithSnapshot(result.cart, result.itemsId);
              } else {
                handleOrderItems(result.orderItems);
                handleRemovedItems(result.removedItems);
              }

              const actualItems = await resolveSuggestedItems(result.itemsId);
              if (streamRequestRef.current !== streamToken) {
                return;
              }

              console.log('🎵 Updating bot message with result:', {
                hasAudio: !!result.audioBase64,
                contentType: result.contentType,
                audioLength: result.audioBase64?.length
              });

              updateBotMessage((prevMessage) => {
                const finalText = normalizeChatbotResponse(
                  result.text || prevMessage.text
                );
                const updatedMessage = {
                  ...prevMessage,
                  text: finalText || prevMessage.text,
                  isStreaming: false,
                  suggestedItemsIds: result.itemsId,
                  suggestedItems: actualItems,
                  ...(result.audioBase64 && {
                    audioBase64: result.audioBase64,
                    contentType: result.contentType || 'audio/wav'
                  })
                };
                console.log('🎵 Updated message:', {
                  hasAudio: !!updatedMessage.audioBase64,
                  contentType: updatedMessage.contentType,
                  messageId: updatedMessage.id
                });
                return updatedMessage;
              });
            } catch (resolveError) {
              console.error(
                "Failed to resolve chatbot suggested items:",
                resolveError
              );
              if (streamRequestRef.current !== streamToken) {
                return;
              }

              updateBotMessage((prevMessage) => {
                const finalText = normalizeChatbotResponse(
                  result.text || prevMessage.text
                );
                return {
                  ...prevMessage,
                  text: finalText || prevMessage.text,
                  isStreaming: false,
                  suggestedItemsIds: result.itemsId,
                  suggestedItems: [],
                  ...(result.audioBase64 && {
                    audioBase64: result.audioBase64,
                    contentType: result.contentType || 'audio/wav'
                  })
                };
              });
            } finally {
              if (streamRequestRef.current === streamToken) {
                setUnreadCount((prev) => prev + 1);
                setIsLoading(false);
              }
            }
          })();
        },
        audioBase64,
        apiContentType ?? undefined,
        () => streamRequestRef.current === streamToken
      );
    } catch (error) {
      console.error("Error sending streaming message:", error);

      // Update bot message with error
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMessageId
            ? {
              ...msg,
              text: "Sorry, I'm having trouble responding right now. Please try again later.",
              isStreaming: false,
            }
            : msg
        )
      );

      setUnreadCount((prev) => prev + 1);
      setIsLoading(false);
    }
  };

  // Send message to chatbot
  const sendMessage = async (message: string) => {
    if (!message.trim() || !branchId) return;

    // Add user message
    const userMessage: ChatbotMessage = {
      id: `user-${Date.now()}`,
      text: message.trim(),
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Choose API function based on configuration
      const apiFunction = sendMessageToApi;

      // Use existing session ID from localStorage
      const sessionId = localStorage.getItem("session_id");

      // Ensure we have a valid session ID
      if (!sessionId) {
        throw new Error("No session ID found - session may not be initialized yet");
      }

      const cartSnapshot = buildChatbotCartSnapshot();

      // Get bot response
      const botResponse = await apiFunction(
        message.trim(),
        branchId,
        sessionId,
        cartSnapshot
      );

      if (
        botResponse.statusCode !== 200 ||
        botResponse.status !== "success" ||
        !botResponse.data
      ) {
        // throw new Error('API response indicates failure');
        const fallbackMessage = botResponse.message ?? "";
        const sanitizedErrorText = normalizeChatbotResponse(fallbackMessage);
        const errorMessage: ChatbotMessage = {
          id: `error-${Date.now()}`,
          text:
            sanitizedErrorText ||
            "Sorry, I'm having trouble responding right now. Please try again later.",
          isUser: false,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, errorMessage]);

        // if (!isOpen) {
        setUnreadCount((prev) => prev + 1);
        // }
        return;
      }

      if (botResponse.data.cart) {
        syncCartWithSnapshot(
          botResponse.data.cart ?? null,
          botResponse.data.items_id ?? undefined
        );
      }

      const safeBotContent = normalizeChatbotResponse(
        botResponse.data.content ?? ""
      );
      const newMessage: ChatbotMessage = {
        id: `bot-${Date.now()}`,
        text: safeBotContent,
        isUser: false,
        timestamp: new Date(),
        suggestedItemsIds: botResponse.data.items_id ?? [],
        suggestedItems: [], // Will be populated with actual items in the component
      };

      // If response contains suggested item IDs, populate with actual items
      if (newMessage.suggestedItemsIds) {
        const actualItems = newMessage.suggestedItemsIds
          ? (newMessage.suggestedItemsIds
            .map((itemId) => getItemDetails(itemId))
            .filter(Boolean) as MenuItem[])
          : [];

        newMessage.suggestedItems = actualItems;
      }

      setMessages((prev) => [...prev, newMessage]);

      // Increment unread count if chat is closed
      // if (!isOpen) {
      setUnreadCount((prev) => prev + 1);
      // }
    } catch (error) {
      console.error("Error sending message:", error);

      // Add error message
      const errorMessage: ChatbotMessage = {
        id: `error-${Date.now()}`,
        text: "Sorry, I'm having trouble responding right now. Please try again later.",
        isUser: false,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);

      // if (!isOpen) {
      setUnreadCount((prev) => prev + 1);
      // }
    } finally {
      setIsLoading(false);
    }
  };

  // Clear all messages
  const clearMessages = () => {
    setMessages([]);
    setUnreadCount(0);
    suggestedItemsCacheRef.current.clear();
  };

  // Open chat
  const openChat = () => {
    setIsOpen(true);
    // setUnreadCount(0); // Mark as read when opened
  };

  // Close chat
  const closeChat = useCallback(() => {
    setIsOpen(false);
    setUnreadCount(0);
  }, []);

  // Toggle chat
  const toggleChat = () => {
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isCartOpen) {
      closeChat();
    }
  }, [closeChat, isCartOpen]);

  // Mark messages as read
  const markAsRead = () => {
    setUnreadCount(0);
  };

  const contextValue: ChatbotContextType = {
    messages,
    isLoading,
    isOpen,
    unreadCount,

    isFullScreen,
    setIsFullScreen,

    sendMessage,
    sendStreamingMessage,
    clearMessages,
    openChat,
    closeChat,
    toggleChat,
    markAsRead,

    getItemDetails,
  };

  return (
    <ChatbotContext.Provider value={contextValue}>
      {children || <Outlet />}
    </ChatbotContext.Provider>
  );
};

// Custom hook to use chatbot context
export const useChatbot = (): ChatbotContextType => {
  const context = useContext(ChatbotContext);
  if (context === undefined) {
    throw new Error("useChatbot must be used within a ChatbotProvider");
  }
  return context;
};
